import { useApolloClient } from '@apollo/client';
import { useCallback, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useForm } from '@/components/react-hook-form';
import { invalidateCacheField } from '@/graphql/client.utils';
// Rows, drawer, data hook, mutation and mapping are shared with the legacy
// trowser's Life insurance step; only the chrome around them differs.
import { useLifeInsuranceStepImportMutation } from '@/modules/household/LinkHouseholdDataTrowser/LifeInsuranceStep/graphql/LifeInsuranceStepImport.generated';
import { LifeInsuranceStepFormShape } from '@/modules/household/LinkHouseholdDataTrowser/LifeInsuranceStep/LifeInsuranceStep.types';
import {
  hasSelectedPolicies,
  mapFormDataToMutations,
  validateSelectedPolicies,
} from '@/modules/household/LinkHouseholdDataTrowser/LifeInsuranceStep/LifeInsuranceStep.utils';
import { EntityKind, EntityStage } from '@/types/schema';

import { PaneForm } from '../../../ManageIntegrationDataModal.types';

export interface LifeInsuranceImportForm extends PaneForm {
  formMethods: UseFormReturn<LifeInsuranceStepFormShape>;
  seed: (values: LifeInsuranceStepFormShape) => void;
  /**
   * The owner-entity kinds and stages the mutation needs. They come from a
   * query the pane owns, so the pane hands them over rather than the form
   * running that query on every step.
   */
  setOwnerEntityData: (
    entityKindById: Record<string, EntityKind>,
    entityStageById: Record<string, EntityStage>,
    subtypeIdByEntityId: Record<string, string>
  ) => void;
}

/** The "Import data" → Life insurance form (eMoney only). */
export function useLifeInsuranceImportForm(
  householdId: string,
  onImported?: () => void
): LifeInsuranceImportForm {
  const apolloClient = useApolloClient();
  const { showFeedback } = useFeedback();
  const successfulPolicyIds = useRef(new Set<string>());

  const formMethods = useForm<LifeInsuranceStepFormShape>({
    defaultValues: { policies: {} },
  });
  const {
    reset,
    handleSubmit,
    clearErrors,
    formState: { isDirty },
  } = formMethods;

  const seed = useCallback(
    (values: LifeInsuranceStepFormShape) => {
      if (isDirty) return;
      reset({
        policies: Object.fromEntries(
          Object.entries(values.policies).filter(
            ([id]) => !successfulPolicyIds.current.has(id)
          )
        ),
      });
    },
    [isDirty, reset]
  );

  const ownerEntityKindsRef = useRef<Record<string, EntityKind>>({});
  const ownerEntityStagesRef = useRef<Record<string, EntityStage>>({});
  const ownerSubtypeIdsRef = useRef<Record<string, string>>({});
  const setOwnerEntityData = useCallback(
    (
      entityKindById: Record<string, EntityKind>,
      entityStageById: Record<string, EntityStage>,
      subtypeIdByEntityId: Record<string, string>
    ) => {
      ownerEntityKindsRef.current = entityKindById;
      ownerEntityStagesRef.current = entityStageById;
      ownerSubtypeIdsRef.current = subtypeIdByEntityId;
    },
    []
  );

  // No `onError`: Apollo only rejects the promise when the caller hasn't
  // handled the error, and the commit needs that rejection to stop.
  const [importPolicies] = useLifeInsuranceStepImportMutation();

  const save = async () => {
    let canContinue = true;
    const submit = handleSubmit(
      async ({ policies }) => {
        if (!hasSelectedPolicies(policies)) return;

        const validationError = validateSelectedPolicies(
          policies,
          formMethods.setError
        );
        if (validationError) {
          showFeedback(validationError, { variant: 'info-high' });
          canContinue = false;
          return;
        }

        const selectedPolicies = Object.entries(policies).filter(
          ([, policy]) => policy.shouldImport
        );
        const results = await Promise.allSettled(
          selectedPolicies.map(async ([id, policy]) => {
            const updateEntities = mapFormDataToMutations(
              { policies: { [id]: policy } },
              householdId,
              ownerEntityKindsRef.current,
              ownerEntityStagesRef.current,
              ownerSubtypeIdsRef.current
            );
            return importPolicies({ variables: { updateEntities } });
          })
        );

        results.forEach((result, index) => {
          const policyId = selectedPolicies[index]?.[0];
          if (policyId && result.status === 'fulfilled') {
            successfulPolicyIds.current.add(policyId);
          }
        });

        if (results.some((result) => result.status === 'fulfilled')) {
          onImported?.();
        }
        invalidateCacheField('entities', apolloClient);
        invalidateCacheField('integrationInsurances', apolloClient);

        reset({
          policies: Object.fromEntries(
            Object.entries(policies).filter(
              ([id]) => !successfulPolicyIds.current.has(id)
            )
          ),
        });

        const failures = results.filter(
          (result): result is PromiseRejectedResult =>
            result.status === 'rejected'
        );
        const firstFailure = failures[0];
        if (firstFailure) {
          showFeedback(
            `${failures.length} insurance ${failures.length === 1 ? 'policy' : 'policies'} failed to import. Please try again.`,
            { variant: 'error' }
          );
          throw firstFailure.reason;
        }
      },
      () => {
        canContinue = false;
      }
    );

    clearErrors();
    await submit();
    return canContinue;
  };

  return { formMethods, save, seed, setOwnerEntityData };
}
